import { useMemo } from 'react';

import { useAxios } from '@/hooks/useAxios';
import { AllIntegrations } from '@/hooks/useIntegration';
import {
  ALL_PLUGINS,
  PIUS_PREFIX_TO_SHORT,
} from '@/sections/customerDrawer/components/pluginConfig';
import { useQuery } from '@/utils/api';

import { extractCredentialFields } from './useEnrichmentPlugins';

// --- Types ---

interface BackendPluginStatus {
  plugin: string;
  name: string;
  hasMasterKey: boolean;
  description: string;
}

interface BackendEnabledStatus {
  plugin: string;
  enabled: boolean;
}

interface BackendGlobalEnabled {
  plugin: string;
  enabled: boolean;
}

export type PluginStatusType =
  | 'active'
  | 'no-key'
  | 'disabled'
  | 'globally-disabled';

export interface OperationsPlugin {
  /** Short ID from pluginConfig, e.g. 'shodan', 'crt-sh' */
  id: string;
  /** pius_* integration ID for paid plugins, undefined for free */
  piusId: string | undefined;
  name: string;
  description: string;
  mode: 'passive' | 'active';
  category: 'domain' | 'cidr' | 'api-key';
  needsApiKey: boolean;
  hasMasterKey: boolean;
  enabled: boolean;
  globalEnabled: boolean;
  status: PluginStatusType;
  credentialFields: ReturnType<typeof extractCredentialFields>;
}

export interface GroupedPlugins {
  paid: OperationsPlugin[];
  free: OperationsPlugin[];
}

// --- Helpers ---

// Build reverse map: short ID -> pius_* ID
const SHORT_TO_PIUS = Object.fromEntries(
  Object.entries(PIUS_PREFIX_TO_SHORT).map(([pius, short]) => [short, pius])
);

function deriveStatus(
  needsApiKey: boolean,
  hasMasterKey: boolean,
  enabled: boolean,
  globalEnabled: boolean
): PluginStatusType {
  if (!globalEnabled) return 'globally-disabled';
  if (!enabled) return 'disabled';
  if (needsApiKey && !hasMasterKey) return 'no-key';
  return 'active';
}

// --- Hook ---

export function useAllDiscoveryPlugins(): {
  data: GroupedPlugins;
  isGloballyEnabled: boolean;
  status: 'pending' | 'success' | 'error';
} {
  const axios = useAxios({ doNotImpersonate: true });

  // Fetch paid plugin master-key status (existing endpoint)
  const enrichmentQuery = useQuery<BackendPluginStatus[]>({
    defaultErrorMessage: 'Failed to fetch enrichment plugins',
    queryKey: ['enrichment', 'plugins'],
    queryFn: async () => {
      const { data } = await axios.get('/enrichment');
      return data;
    },
  });

  // Fetch enabled/disabled state for all plugins (new endpoint)
  const enabledQuery = useQuery<BackendEnabledStatus[]>({
    defaultErrorMessage: 'Failed to fetch plugin enabled states',
    queryKey: ['enrichment', 'enabled'],
    queryFn: async () => {
      const { data } = await axios.get('/enrichment/enabled');
      return data;
    },
    retry: false,
  });

  // Fetch master kill switch state
  const globalEnabledQuery = useQuery<BackendGlobalEnabled>({
    defaultErrorMessage: 'Failed to fetch global discovery state',
    queryKey: ['enrichment', 'global', 'enabled'],
    queryFn: async () => {
      const { data } = await axios.get('/enrichment/global/enabled');
      return data;
    },
    retry: false,
  });

  const data = useMemo(() => {
    const enrichmentMap = new Map(
      (enrichmentQuery.data ?? []).map(p => [p.plugin, p])
    );
    const enabledMap = new Map(
      (enabledQuery.data ?? []).map(p => [p.plugin, p.enabled])
    );
    // Fail-open: if global switch query fails or is not yet loaded, treat as enabled
    const isGloballyEnabled = globalEnabledQuery.data?.enabled ?? true;

    const plugins: OperationsPlugin[] = ALL_PLUGINS.map(def => {
      const piusId = SHORT_TO_PIUS[def.id];
      const backend = piusId ? enrichmentMap.get(piusId) : undefined;
      const hasMasterKey = backend?.hasMasterKey ?? false;
      // Default: enabled=true for all plugins
      // Free plugins are always "enabled" — no backend state yet
      const enabled = piusId ? (enabledMap.get(piusId) ?? true) : true;
      const description = backend?.description || def.description;

      const integration = piusId
        ? AllIntegrations[piusId as keyof typeof AllIntegrations]
        : undefined;

      return {
        id: def.id,
        piusId,
        name: def.name,
        description,
        mode: def.mode,
        category: def.category,
        needsApiKey: def.needsApiKey,
        hasMasterKey,
        enabled,
        globalEnabled: isGloballyEnabled,
        status: deriveStatus(
          def.needsApiKey,
          hasMasterKey,
          enabled,
          isGloballyEnabled
        ),
        credentialFields: extractCredentialFields(integration?.inputs),
      };
    });

    return {
      paid: plugins.filter(p => p.needsApiKey),
      free: plugins.filter(p => !p.needsApiKey),
    };
  }, [enrichmentQuery.data, enabledQuery.data, globalEnabledQuery.data]);

  // Only the enrichment query (existing endpoint) drives error state.
  // The enabled query is optional — if it fails, all plugins default to enabled.
  const status =
    enrichmentQuery.status === 'pending'
      ? 'pending'
      : enrichmentQuery.status === 'error'
        ? 'error'
        : 'success';

  const isGloballyEnabled = globalEnabledQuery.data?.enabled ?? true;

  return { data, isGloballyEnabled, status };
}
